import { useMutation } from '@apollo/client'
import {
  Button,
  Divider,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material'
import {
  Mutation,
  StripePaymentIntent,
  User,
} from '@promoboxx/graphql-gateway-types'
import { Formik, Field, Form } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { formatWithCents } from '@src/lib/Utils'
import { userSelector } from '@src/stores/app/selectors'

import { calculateFee, CREATE_STRIPE_PAYMENT_INTENT } from './stripe'

const schema = Yup.object().shape({
  maxDepositAmount: Yup.number(),
  amount: Yup.number()
    .required()
    .min(1)
    .when('maxDepositAmount', (maxDepositAmount: number, schema: any) => {
      if (maxDepositAmount) {
        return schema.max(maxDepositAmount)
      }
      return schema
    })
    .label(I18n.t('content.payment.depositAmount')),
})
// A payment intent must be created prior to rendering the StripePaymentForm as it requires a client_secret and an amount to charge
const SetupPaymentIntent: React.FC<
  React.PropsWithChildren<{
    amountInCents?: number
    businessId: string
    onComplete: (value: StripePaymentIntent) => void
    maxDepositAmount?: number
  }>
> = ({ amountInCents, businessId, onComplete, maxDepositAmount = 10000 }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [createPaymentIntent] = useMutation<Mutation>(
    CREATE_STRIPE_PAYMENT_INTENT,
  )
  const currentUser: User = useSelector(userSelector)

  return (
    <Formik
      initialValues={{
        maxDepositAmount,
        amount: amountInCents ? Number(amountInCents) / 100 : 0,
      }}
      isInitialValid={amountInCents !== undefined && amountInCents >= 100}
      onSubmit={async (values, formik) => {
        // There was some work here originally to handle existing payment intents to handle if the user changed their mind about the amount to deposit,
        // after some discussion with BE we'll just create a new one. Ideally, we'll re-address this in the future.
        try {
          const amount_cents = Number(values.amount) * 100
          const { data } = await createPaymentIntent({
            variables: {
              data: {
                amount_cents: `${amount_cents}`,
                currency: 'USD',
                payment_type: 'card',
                type: 'business',
                type_id: businessId,
                user_email: currentUser.email,
                user_id: Number(currentUser.id),
              },
            },
          })

          if (!data?.createPaymentIntent) {
            throw new Error('Error creating payment intent')
          }
          formik.setSubmitting(false)
          onComplete({
            ...data.createPaymentIntent,
            amount: `${data.createPaymentIntent.amount}`,
            final_charge_amount: `${data.createPaymentIntent.final_charge_amount}`,
          })
        } catch (e) {
          if (e instanceof Error) {
            enqueueSnackbar(e?.message, {
              variant: 'error',
            })
          }
          formik.setSubmitting(false)
        }
      }}
      validationSchema={schema}
    >
      {({ values, isSubmitting, isValid }) => (
        <Form>
          <Stack direction="column" sx={{ paddingTop: 4 }} spacing={2}>
            <Field
              component={TextField}
              name="amount"
              type="number"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              min={1}
              value={
                values.amount
                  ? `${values.amount}`.replace(/^0+/, '')
                  : values.amount
              }
              label={I18n.t('content.payment.depositAmount')}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
            <Divider />
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Typography fontWeight="bold" variant="body2">
                {I18n.t('content.payment.fee')}*
              </Typography>
              <Typography fontWeight="bold" variant="body2">
                {formatWithCents.format(
                  calculateFee(values.amount * 100).feeAmount / 100,
                )}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Typography fontWeight="bold" variant="body2">
                {I18n.t('content.payment.total')}
              </Typography>
              <Typography fontWeight="bold" variant="body2">
                {formatWithCents.format(
                  calculateFee(values.amount * 100).totalAmount / 100,
                )}
              </Typography>
            </Stack>
            <Divider />

            <Button
              type="submit"
              color="primary"
              variant="contained"
              fullWidth
              disabled={isSubmitting || !isValid}
            >
              {I18n.t('content.payment.goToPaymentDetails')}
            </Button>

            <Typography variant="caption" color="textSecondary">
              {I18n.t('content.payment.feeDescription')}
            </Typography>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
export default SetupPaymentIntent
